import { PoseLandmark } from '../types';

/**
 * Geometric utility functions for gesture recognition.
 * 
 * These functions provide the mathematical foundation for detecting body gestures
 * by analyzing spatial relationships between pose landmarks.
 * 
 * Key concepts:
 * - Screen coordinates: (0,0) is top-left, y increases downward
 * - Normalized coordinates: All positions in [0, 1] range
 * - Angles: Measured in degrees for readability
 * 
 * Validates Requirements 3.3.1, 3.3.2, 3.3.3
 */

/**
 * Calculate Euclidean distance between two landmarks.
 * Uses 2D distance (x, y only) in normalized coordinate space.
 * 
 * @param p1 First landmark
 * @param p2 Second landmark
 * @returns Distance in normalized units [0, ~1.4]
 * 
 * @example
 * const dist = distance(leftWrist, rightWrist);
 * if (dist < 0.1) console.log('Wrists are close together');
 */
export function distance(p1: PoseLandmark, p2: PoseLandmark): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate angle between three landmarks (in degrees).
 * The angle is measured at the middle point (p2), which acts as the vertex.
 * 
 * Useful for detecting arm extension (shoulder-elbow-wrist angle near 180°)
 * or bent arms (angle significantly less than 180°).
 * 
 * @param p1 First landmark
 * @param p2 Middle landmark (vertex of angle)
 * @param p3 Third landmark
 * @returns Angle in degrees [0-180]
 * 
 * @example
 * // Check if arm is straight
 * const armAngle = angleBetweenPoints(shoulder, elbow, wrist);
 * const isArmStraight = armAngle > 150; // Nearly 180° = straight
 */
export function angleBetweenPoints(p1: PoseLandmark, p2: PoseLandmark, p3: PoseLandmark): number {
  // Vector from p2 to p1
  const v1x = p1.x - p2.x;
  const v1y = p1.y - p2.y;
  
  // Vector from p2 to p3
  const v2x = p3.x - p2.x;
  const v2y = p3.y - p2.y;
  
  // Calculate dot product
  const dot = v1x * v2x + v1y * v2y;
  
  // Calculate magnitudes
  const mag1 = Math.sqrt(v1x * v1x + v1y * v1y);
  const mag2 = Math.sqrt(v2x * v2x + v2y * v2y);
  
  // Avoid division by zero
  if (mag1 === 0 || mag2 === 0) {
    return 0;
  }
  
  // Calculate angle in radians, then convert to degrees
  const cosAngle = dot / (mag1 * mag2);
  // Clamp to [-1, 1] to handle floating point errors
  const clampedCos = Math.max(-1, Math.min(1, cosAngle));
  const angleRad = Math.acos(clampedCos);
  const angleDeg = angleRad * (180 / Math.PI);
  
  return angleDeg;
}

/**
 * Check if landmark p1 is above landmark p2 in screen space.
 * 
 * Note: In screen coordinates, y=0 is at the top, so "above" means smaller y value.
 * 
 * @param p1 First landmark
 * @param p2 Second landmark
 * @returns True if p1 is above p2 (p1.y < p2.y)
 * 
 * @example
 * // Check if wrist is above shoulder (arm raised)
 * if (isAbove(wrist, shoulder)) {
 *   console.log('Arm is raised');
 * }
 */
export function isAbove(p1: PoseLandmark, p2: PoseLandmark): boolean {
  return p1.y < p2.y;
}

/**
 * Check if three landmarks are approximately collinear (form a straight line).
 * 
 * Points are considered collinear if the angle between them is close to 180°.
 * Used to detect extended arms (shoulder-elbow-wrist should be nearly straight).
 * 
 * @param p1 First landmark
 * @param p2 Middle landmark
 * @param p3 Third landmark
 * @param threshold Angle deviation tolerance in degrees (default 30°)
 * @returns True if angle is within threshold of 180° (straight line)
 * 
 * @example
 * // Check if arm is extended (shoulder-elbow-wrist collinear)
 * if (areCollinear(shoulder, elbow, wrist, 30)) {
 *   console.log('Arm is extended');
 * }
 */
export function areCollinear(
  p1: PoseLandmark,
  p2: PoseLandmark,
  p3: PoseLandmark,
  threshold: number = 30
): boolean {
  const angle = angleBetweenPoints(p1, p2, p3);
  // Points are collinear if angle is close to 180 degrees
  return angle >= (180 - threshold);
}

/**
 * Check if a landmark has sufficient visibility for reliable use.
 * 
 * MediaPipe provides a visibility score [0, 1] for each landmark.
 * Low visibility indicates the landmark may be occluded or poorly detected.
 * 
 * @param point Landmark to check
 * @param minVisibility Minimum acceptable visibility (default 0.5)
 * @returns True if landmark visibility >= threshold
 * 
 * @example
 * if (isVisible(wrist) && isVisible(elbow)) {
 *   // Safe to use these landmarks for gesture detection
 * }
 */
export function isVisible(point: PoseLandmark, minVisibility: number = 0.5): boolean {
  return point.visibility >= minVisibility;
}

/**
 * Calculate the angle of a line relative to horizontal (in degrees).
 * 
 * Returns angle in standard mathematical convention:
 * - 0° = pointing right (horizontal)
 * - 90° = pointing down
 * - -90° = pointing up
 * - ±180° = pointing left
 * 
 * @param p1 Start landmark
 * @param p2 End landmark
 * @returns Angle in degrees [-180, 180]
 * 
 * @example
 * const angle = angleFromHorizontal(shoulder, wrist);
 * if (Math.abs(angle) < 30) {
 *   console.log('Arm is approximately horizontal');
 * }
 */
export function angleFromHorizontal(p1: PoseLandmark, p2: PoseLandmark): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const angleRad = Math.atan2(dy, dx);
  const angleDeg = angleRad * (180 / Math.PI);
  return angleDeg;
}

/**
 * Calculate the angle of a line relative to vertical (in degrees).
 * 
 * Useful for detecting vertical arm raises.
 * Returns absolute angle from vertical axis:
 * - 0° = pointing straight up
 * - 90° = horizontal
 * - Values near 0° indicate vertical alignment
 * 
 * @param p1 Start landmark (typically lower point, e.g., shoulder)
 * @param p2 End landmark (typically upper point, e.g., wrist)
 * @returns Angle in degrees [0, 180]
 * 
 * @example
 * const angle = angleFromVertical(shoulder, wrist);
 * if (angle < 30) {
 *   console.log('Arm is raised vertically');
 * }
 */
export function angleFromVertical(p1: PoseLandmark, p2: PoseLandmark): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  // Calculate angle from vertical axis
  // Vertical up is when dx=0 and dy<0 (negative y is up in screen coordinates)
  const angleRad = Math.atan2(Math.abs(dx), -dy);
  const angleDeg = angleRad * (180 / Math.PI);
  return Math.abs(angleDeg);
}

/**
 * Test if a point is inside a polygon using ray casting algorithm.
 * 
 * The ray casting algorithm works by casting a ray from the test point
 * to infinity and counting how many times it crosses the polygon boundary.
 * If the count is odd, the point is inside; if even, it's outside.
 * 
 * @param point Point to test {x, y}
 * @param vertices Array of polygon vertices [{x, y}, ...]
 * @returns True if point is inside the polygon
 * 
 * @example
 * const polygon = [{x: 0, y: 0}, {x: 1, y: 0}, {x: 1, y: 1}, {x: 0, y: 1}];
 * const inside = isPointInPolygon({x: 0.5, y: 0.5}, polygon); // true
 */
export function isPointInPolygon(
  point: { x: number; y: number },
  vertices: Array<{ x: number; y: number }>
): boolean {
  if (vertices.length < 3) {
    return false; // Not a valid polygon
  }
  
  let inside = false;
  const x = point.x;
  const y = point.y;
  
  // Ray casting algorithm
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const vi = vertices[i];
    const vj = vertices[j];
    if (!vi || !vj) continue;
    
    const xi = vi.x;
    const yi = vi.y;
    const xj = vj.x;
    const yj = vj.y;
    
    // Check if ray from point crosses edge (i, j)
    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    
    if (intersect) {
      inside = !inside;
    }
  }
  
  return inside;
}
